<template lang="pug">
  .content-index
    .sticky-content.column
      .index-header.column.m-top-4
        h2.text-big.bold.m4-bottom-7 {{ innerI18n.t('sidebar.index') + ":" }}
        .column.m-left-1
          router-link.text-normal.bold.index-anchor(
            exact-active-class='current'
            :to='triggerLink'
          ) {{ innerI18n.t('krea.resource.sidebar.trigger') }}
          .column(v-if='showTriggerSubindex')
            a.subindex-anchor.text-normal.bold(
              v-for='item in availableTriggerIndex'
              href=''
              :class='{ current: inSection(item) }'
              @click.prevent='scrollToSection($event, item)'
            ) {{ innerI18n.t(`krea.resource.sidebar.${item}`) }}
          .column(v-for='resClass in classesCount')
            router-link.class-item.text-normal.bold.index-anchor(exact-active-class='current' :to='classLink(resClass)')
              | {{ `${innerI18n.t('krea.resource.sidebar.class')} ${resClass}` }}
              button.m-left-2.delete-icon(v-if='canDeleteClass' @click.prevent='() => showDeleteClassModal(resClass)')
                simple-svg.black-icon.m4-bottom-05(:filepath='trashIcon' width='18px' height='18px')
            .column(v-if='showClassSubindex(resClass)')
              a.subindex-anchor.text-normal.bold(
                v-for='item in availableClassesIndex'
                href=''
                :class='{ current: inSection(item) }'
                @click.prevent='scrollToSection($event, item)'
              ) {{ innerI18n.t(`krea.resource.sidebar.${item}`) }}
          router-link.text-normal.bold.index-anchor(
            exact-active-class='current'
            v-if='!isStudentTab'
            :to='{name: routes.kreaResourceTeacherValuation}'
          ) {{ innerI18n.t('krea.resource.sidebar.globalValuation') }}
      button.add-class.row.middle(@click='addNewClass')
        h2.text-normal.bold {{ innerI18n.t("krea.resource.sidebar.createClass") }}
        simple-svg.icon.white-icon.m-left-1.m4-top-1(:filepath='addIcon' width='10px' height='10px')
      .index-content.m-top-1
        author-bio-tooltip.bottom(:author='author' styleClass='author-bio-popover')
          .author.row(v-if='author')
            img.author-avatar(:src='author.picture || defaultImage' alt='author avatar')
            .column
              h4.text-big.m-bottom-1 {{ author.name }}
              h6.text-normal.dark-gray {{ author.bio }}
</template>

<script>
import { innerI18n } from '@/main'
import signalIcon from '@/assets/ic_radio.svg'
import defaultImage from '@/assets/empty_profile.svg'
import addIcon from '@/assets/ic_add.svg'
import previewIcon from '@/assets/ic_preview.svg'
import { studentClassesIndex, teacherClassesIndex, studentTriggerIndex, teacherTriggerIndex } from '@/utils/constants'
import AuthorBioTooltip from '@/components/AuthorBioTooltip'
import { routes } from '../../../router'
import trashIcon from '@/assets/krea/ic_trash.svg'
import { mapActions, mapState } from 'vuex'
import { languagesCodesToString } from '../../../constants/languages'

export default {
  components: { AuthorBioTooltip },
  props: {
    resourceId: { type: Number, required: false },
    author: { type: Object, required: false },
    resourceTitle: { type: String, required: false },
    resourceWebinar: { type: Boolean, required: false },
    classes: { type: Number, required: false },
    studentPreviewLink: { type: String },
    categoryName: { type: String },
    isStudentView: { type: Boolean },
    isStudentTab: { type: Boolean }
  },
  data() {
    return {
      signalIcon,
      trashIcon,
      defaultImage,
      addIcon,
      sectionsHeights: [],
      previewIcon,
      innerI18n,
      routes,
      navbarHeight: 90
    }
  },
  computed: {
    ...mapState({
      resource: state => state.kreaResource.resource,
      activeLanguage: state => state.kreaResource.activeLanguage
    }),
    activeLocalizedResource() {
      return this.resource.localizedResources.find(res => languagesCodesToString[res.locale] === this.activeLanguage)
    },
    canDeleteClass() {
      return this.classes > 1
    },
    classesCount() {
      return [...Array(this.classes + 1).keys()].slice(1)
    },
    availableTriggerIndex() {
      if (this.isStudentTab) {
        return studentTriggerIndex.filter(item => {
          if (item === 'classPlanDescription') {
            return this.activeLocalizedResource.classPlans.length > 1 ||
              this.activeLocalizedResource.classPlanDescription
          } else return true
        })
      } else {
        return teacherTriggerIndex.filter(item => {
          if (item === 'classPlanDescription') {
            return this.activeLocalizedResource.classPlans.length > 1 ||
              this.activeLocalizedResource.classPlanDescription
          } else return true
        })
      }
    },
    availableClassesIndex() {
      return this.isStudentTab
        ? studentClassesIndex
        : teacherClassesIndex
    },
    showTriggerSubindex() {
      return this.isStudentTab
        ? this.$route.name === routes.kreaResourceStudentTrigger
        : this.$route.name === routes.kreaResourceTeacherTrigger
    },
    triggerLink() {
      return this.isStudentTab
        ? { name: routes.kreaResourceStudentTrigger }
        : { name: routes.kreaResourceTeacherTrigger }
    }
  },
  mounted() {
    setTimeout(() => {
      this.$nextTick(() => {
        this.setSectionsHeights()
      })
    }, 500)
    window.addEventListener('scroll', this.handleScroll)
  },
  destroyed() {
    window.removeEventListener('scroll', this.handleScroll)
  },
  methods: {
    ...mapActions([
      'showElement',
      'addKreaResourceClass',
      'removeKreaResourceClass'
    ]),
    showDeleteClassModal(classNumber) {
      this.showElement({
        element: 'deleteKreaClassModal',
        props: { classNumber }
      })
    },
    deleteClass(classNumber) {
      this.removeKreaResourceClass({ locale: this.activeLanguage, classNumber })
      if (this.$route.name === routes.kreaResourceStudentClass) this.$router.push({ name: routes.kreaResourceStudentTrigger })
      else this.$router.push({ name: routes.kreaResourceTeacherTrigger })
    },
    showClassSubindex(resClass) {
      return this.isStudentTab
        ? this.$route.name === routes.kreaResourceStudentClass && Number(this.$route.params.classId) === resClass
        : this.$route.name === routes.kreaResourceTeacherClass && Number(this.$route.params.classId) === resClass
    },
    classLink(resClass) {
      return this.isStudentTab
        ? { name: routes.kreaResourceStudentClass, params: { classId: resClass } }
        : { name: routes.kreaResourceTeacherClass, params: { classId: resClass } }
    },
    scrollToSection(e, section) {
      e.preventDefault()
      if (document.getElementById(`${section}`)) {
        window.scrollTo(0, document.getElementById(`${section}`).offsetTop - this.navbarHeight)
      }
    },
    addSection(elem) {
      const section = {
        name: elem.id,
        starts: elem.offsetTop,
        ends: elem.offsetTop + elem.offsetHeight,
        current: false
      }
      this.sectionsHeights.push(section)
    },
    setSectionsHeights() {
      const addSection = section => {
        const elem = document.querySelectorAll(`[id^=${section}]`)
        elem.forEach(element => this.addSection(element))
      }
      if (this.isStudentTab) studentTriggerIndex.forEach(addSection)
      else teacherTriggerIndex.forEach(addSection)
      if (this.isStudentTab) studentClassesIndex.forEach(addSection)
      else teacherClassesIndex.forEach(addSection)
    },
    handleScroll() {
      const { scrollY } = window
      this.sectionsHeights.map(elem => {
        elem.current = (scrollY + this.navbarHeight) >= elem.starts && (scrollY + this.navbarHeight) < elem.ends
      })
    },
    inSection(sectionName) {
      const filterSection = this.sectionsHeights.filter(section => section.name === sectionName)
      if (filterSection.length > 0) {
        return filterSection[0].current
      }
      return false
    },
    isSectionInResource(sectionName) {
      return this.sectionsHeights.filter(section => section.name === sectionName).length > 0
    },
    addNewClass() {
      this.addKreaResourceClass(this.activeLanguage)
      this.$router.push({ name: routes.kreaResourceTeacherClass, params: { classId: this.classes + 1 } })
    }
  }
}
</script>

<style lang="scss" scoped>
@import 'src/scss/variables/colors';
@import 'src/scss/variables/sizes';

.sticky-content{
  position: sticky;
  top: $navbar-height;
}

.add-class{
  .icon{
    padding: 5px;
    background-color: $blue;
    border-radius: 50%;
  }
}

%index-item {
  background-color: $dark-gray;
  content: '';
  width: 8px;
  height: 8px;
  margin-right: 20px;
  position: relative;
}

.author-avatar{
  box-sizing: border-box;
  height: 40px;
  width: 40px;
  border: 1px solid $light-gray;
  border-radius: 100%;
  margin-right: 25px;
}

.author {
  padding: 20px 0;
}

.index-anchor {
  align-items: center;
  display: flex;
  margin-bottom: 20px;

  &::before {
    @extend %index-item;
    border-radius: 100%;
  }
}

.subindex-anchor {
  align-items: center;
  display: flex;
  margin: 0 30px 20px;

  &::before {
    @extend %index-item;
    border-radius: 50%;
  }
}

.content-index {
  max-width: 420px;
  width: 100%;
}

.student-content-index {
  max-width: 900px;
}

.current {
  font-weight: bold;
  color: $blue;

  &::before {
    @extend %index-item;
    background-color: $blue;
  }
}

.student-container-option:nth-child(n+2) {
  border-top: 1px solid $light-gray;
}

.student-title {
  padding-left: 25px;
}

.student-option {
  color: $dark-gray;
  font-weight: normal;
  margin-bottom: 0;
  padding: 16px 25px;

  &::before {
    content: none;
  }

  &.current {
    color: $blue;
  }
}

.student-subsection {
  font-size: 14px;
  font-weight: 300;
  line-height: 17px;

  &::before {
    border: 1px solid $dark-gray;
    height: 6px;
    margin-right: 10px;
    width: 6px;
  }

  &.current {
    font-weight: bold;
    color: $blue;

    &::before {
      background-color: $blue;
      border: 1px solid $blue;
    }
  }
}

.class-item{
  .delete-icon{
    display: none;
  }
  &:hover{
    .delete-icon{
      display: block;
    }
  }
}
</style>
